import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  getCareSourceKnowledgeSuggestions,
  sortCareSourcesByNewest,
} from './careSourceStatusModel';
import { resolveTitle } from '../../shared/sourceTitleResolver';
import { buildNeutralDocumentCandidatesFromFindings } from '../../utils/documentCandidateNormalization';
import { buildTypedDocumentCandidatesFromNeutralCandidates } from '../../utils/documentFactTyping';
import { buildDocumentDestinationCandidatesFromFactCandidates } from '../../utils/documentFactDestinationResolution';

const SOURCE_NOTES_COLLECTION = 'careSources';

const DOCUMENT_SOURCE_STATUSES = new Set([
  'captured',
  'organizing',
  'saved',
  'needs_review',
  'partially_organized',
  'organized',
  'reviewed',
  'saved_source',
  'extraction_failed',
]);

const getSourceStatus = (source = {}) => String(
  source.status
  || source.processingStatus
  || source.metadata?.careSourceProcessing?.status
  || ''
).trim();

const getSourceContext = (source = {}) => String(
  source.sourceContext
  || source.sourceMetadata?.sourceContext
  || source.metadata?.source?.sourceContext
  || ''
).trim();

const getSourceKind = (source = {}) => String(
  source.sourceKind
  || source.sourceMetadata?.sourceKind
  || source.metadata?.source?.sourceKind
  || ''
).trim();

export const isFormalCareDocumentSource = (source = {}) => {
  const sourceKind = getSourceKind(source);
  if (sourceKind === 'file' || sourceKind === 'image') return true;
  if (getSourceContext(source) === 'document') return true;
  return hasDocumentRoute(source);
};

const hasKnowledgeSignals = (source = {}) => (
  getCareSourceKnowledgeSuggestions(source).length > 0
  || Number(source.careKnowledgeSuggestionSummary?.itemCount || 0) > 0
  || Boolean(source.careKnowledgeReviewCompletedAt)
);

const getSourceText = (source = {}) => String(
  source.rawText
  || source.originalText
  || source.sourceText
  || source.cleanedText
  || ''
).trim();

const getSourceUnderstandingPayloads = (source = {}) => {
  const payloads = [
    source.sourceUnderstanding,
    source.metadata?.careSourceProcessing?.sourceUnderstanding,
    source.careKnowledgeSuggestionSummary?.sourceUnderstanding,
  ].filter(Boolean);

  return payloads.flatMap((payload) => (
    payload?.understanding ? [payload, payload.understanding] : [payload]
  ));
};

const hasDocumentRoute = (source = {}) => getSourceUnderstandingPayloads(source).some((payload = {}) => (
  payload.hasDocumentReference === true
  || (payload.processingBuckets || []).some((bucketRoute = {}) => bucketRoute.bucket === 'document')
  || (payload.artifactRoutes || []).some((artifactRoute = {}) => (
    artifactRoute.artifactType === 'document_reference'
    || artifactRoute.artifactType === 'source_only'
  ))
));

export const inferCareDocumentType = (source = {}) => {
  const explicitType = String(
    source.sourceMetadata?.documentTypeLabel
    || source.metadata?.source?.documentTypeLabel
    || source.documentTypeLabel
    || ''
  ).trim();
  if (explicitType) return explicitType;

  const text = getSourceText(source).toLowerCase();
  if (/\bbehavior support plan\b/.test(text)) return 'Behavior Plan';
  if (/\biep\b|\bindividualized education program\b|\bannual goal\b/.test(text)) return 'IEP';
  if (/\bpreferred foods\b|\bfoods working on\b|\bfeeding plan\b/.test(text)) return 'Feeding Plan';
  if (/\bmedication instructions?\b|\bmedication\b/.test(text)) return 'Medication Instructions';
  if (/\btherapy recommendation\b|\btherapy\b/.test(text)) return 'Therapy Note';
  return 'Source';
};

export const inferCareDocumentTitle = (source = {}) => {
  return resolveTitle(source, 'library');
};

export const shouldShowCareDocumentSource = (source = {}) => {
  if (!source || source.status === 'deleted' || source.archived === true) return false;
  if (getSourceContext(source) === 'profile') return false;
  if (!isFormalCareDocumentSource(source)) return false;

  const status = getSourceStatus(source);
  return DOCUMENT_SOURCE_STATUSES.has(status) || hasKnowledgeSignals(source);
};

const getPersistedDocumentFindings = (source = {}) => (
  source.documentFindings
  || source.metadata?.careSourceProcessing?.documentFindings
  || source.metadata?.captureFirst?.documentFindings
  || null
);

const getPersistedDocumentPurpose = (source = {}) => (
  source.documentPurpose
  || source.metadata?.careSourceProcessing?.documentPurpose
  || source.metadata?.captureFirst?.documentPurpose
  || null
);

export const normalizeCareDocumentSource = (source = {}) => {
  const documentPurpose = getPersistedDocumentPurpose(source);
  const documentCandidates = buildNeutralDocumentCandidatesFromFindings({
    originId: source.id || '',
    childId: source.childId || '',
    findings: getPersistedDocumentFindings(source) || {},
    documentPurpose,
  });

  const documentFactCandidates = buildTypedDocumentCandidatesFromNeutralCandidates(documentCandidates);

  return {
    id: source.id || '',
    ...source,
    documentPurpose,
    documentCandidates,
    documentFactCandidates,
    documentDestinationCandidates: buildDocumentDestinationCandidatesFromFactCandidates(documentFactCandidates),
    careDocumentTitle: inferCareDocumentTitle(source),
    careDocumentType: inferCareDocumentType(source),
  };
};

export const subscribeToCareDocumentSources = ({
  childId = '',
  onSources = () => {},
  onError = () => {},
} = {}, deps = {}) => {
  const cleanChildId = String(childId || '').trim();
  if (!cleanChildId) {
    onSources([]);
    return () => {};
  }

  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const queryFn = deps.query || query;
  const whereFn = deps.where || where;
  const snapshotFn = deps.onSnapshot || onSnapshot;
  const sourceQuery = queryFn(
    collectionFn(firestoreDb, SOURCE_NOTES_COLLECTION),
    whereFn('childId', '==', cleanChildId)
  );

  return snapshotFn(
    sourceQuery,
    (snapshot) => {
      const sources = snapshot.docs
        .map((sourceDoc) => normalizeCareDocumentSource({
          id: sourceDoc.id,
          ...sourceDoc.data(),
        }))
        .filter(shouldShowCareDocumentSource);

      onSources(sortCareSourcesByNewest(sources));
    },
    onError
  );
};
